import { Add, Delete } from '@mui/icons-material';
import {
    Box,
    Button,
    Grid,
    IconButton,
    Typography,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale';
import React from 'react';
import InstallmentsSelect from './InstallmentsSelect';
import PaymentMethodSelect from './PaymentMethodSelect';
import MoneyInput from '../MoneyInput';
import { formatCurrencyInCents } from '../../helpers/formatters';
import {
    createPaymentRow,
    paymentRowsTotalCents,
} from '../../helpers/checkoutPayments';

export default function CombinedPaymentsEditor({
    payments = [],
    onChange,
    totalAmountCents = 0,
    defaultDate,
}) {
    const updateRow = (index, patch) => {
        const next = payments.map((row, i) => {
            if (i !== index) {
                return row;
            }
            const merged = { ...row, ...patch };
            if (Object.prototype.hasOwnProperty.call(patch, 'name') && patch.name !== 'Crédito') {
                merged.installments = 1;
            }
            return merged;
        });
        onChange(next);
    };

    const addPayment = () => {
        const paid = paymentRowsTotalCents(payments);
        const remaining = Math.max(0, totalAmountCents - paid);
        onChange([
            ...payments,
            createPaymentRow({
                name: 'Dinheiro',
                amountCents: remaining,
                paymentDate: defaultDate || new Date(),
            }),
        ]);
    };

    const removePayment = (index) => {
        if (payments.length <= 1) {
            return;
        }
        onChange(payments.filter((_, i) => i !== index));
    };

    const paidCents = paymentRowsTotalCents(payments);
    const remainingCents = totalAmountCents - paidCents;

    return (
        <Box sx={{ width: '100%' }}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                {payments.map((row, index) => (
                    <Grid container spacing={2} key={row.key} sx={{ mb: 1 }} alignItems="flex-start">
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" color="text.secondary">
                                Pagamento
                                {' '}
                                {index + 1}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <PaymentMethodSelect
                                label="Meio de pagamento"
                                value={row.name}
                                onChange={(e) => updateRow(index, { name: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={2}>
                            <InstallmentsSelect
                                label="Parcelas"
                                value={row.installments}
                                onChange={(e) => updateRow(index, { installments: e.target.value })}
                                disabled={row.name !== 'Crédito'}
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <MoneyInput
                                label="Valor"
                                value={row.amountReais}
                                onChange={(value) => updateRow(index, { amountReais: value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <DatePicker
                                label="Data de pagamento"
                                value={row.paymentDate}
                                onChange={(newDate) => updateRow(index, { paymentDate: newDate })}
                                slotProps={{ textField: { fullWidth: true } }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={1} sx={{ display: 'flex', alignItems: 'center' }}>
                            <IconButton
                                color="error"
                                onClick={() => removePayment(index)}
                                disabled={payments.length <= 1}
                                aria-label={`remover pagamento ${index + 1}`}
                            >
                                <Delete />
                            </IconButton>
                        </Grid>
                    </Grid>
                ))}
            </LocalizationProvider>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mt: 1 }}>
                <Button type="button" startIcon={<Add />} onClick={addPayment}>
                    Adicionar pagamento
                </Button>
                <Typography variant="body2" color="text.secondary">
                    Soma:
                    {' '}
                    <strong>{formatCurrencyInCents(paidCents)}</strong>
                    {totalAmountCents > 0 && (
                        <>
                            {' '}
                            / Total
                            {' '}
                            {formatCurrencyInCents(totalAmountCents)}
                            {remainingCents !== 0 && (
                                <>
                                    {' '}
                                    (
                                    {remainingCents > 0 ? 'faltam' : 'excede'}
                                    {' '}
                                    {formatCurrencyInCents(Math.abs(remainingCents))}
                                    )
                                </>
                            )}
                        </>
                    )}
                </Typography>
            </Box>
        </Box>
    );
}
